import React, { useState, useEffect, useMemo } from 'react';
import moment from 'moment';
import { v4 as uuid } from 'uuid';
import { sum } from 'lodash';
import NumberFormat from 'react-number-format';

import Tooltip from '@material-ui/core/Tooltip';
import { makeStyles } from '@material-ui/core/styles';

import LabelIcon from '../../assets/icons/LabelIcon';

import LeadProcedureItem from './LeadProcedureItem';
import Modal from '../shared/modal';
import TextInput from '../shared/TextInput';
import ColorButton from '../shared/ColorButton';
import SelectBox from '../shared/select/SelectBox';
import Autocomplete from '../shared/autocomplete/NewAutocomplete';
import MultiSelect from '../shared/select/MultiSelect';
import DatePicker from '../shared/date-picker/NewDatePicker';
import Checkbox from '../shared/Checkbox';

import {
  anticipatedVolumeTypes,
  LOST_LEADS_PIPELINE_ID,
  ACTIVE_SURGEONS_PIPELINE_ID,
  leadStatuses, defaultRankingAndActivity,
} from '../../constants/sales'

import { defaultStaticColor } from '../../styles/theme/palette';

const titleOptions = [
  { label: 'Dr', value: 'Dr' },
  { label: 'Mr', value: 'Mr' },
  { label: 'Ms', value: 'Ms' },
  { label: 'Mrs', value: 'Mrs' },
  { label: 'Prof.', value: 'Prof.' },
  { label: 'Assc Prof.', value: 'Assc Prof.' },
];

const useStyles = makeStyles(() => ({
  tooltip: {
    fontSize: '12px',
    padding: '4px 8px',
    borderRadius: 8
  }
}));

const rankingOptions = [
  { label: '1', value: 1 },
  { label: '2', value: 2 },
  { label: '3', value: 3 },
  { label: '4', value: 4 },
  { label: '5', value: 5 },
];

const LeadModal = (props) => {
  const {
    open,
    onClose,
    onSubmit,
    loading,
    mode = 'create',
    lead,
    surgeonsList = [],
    proceduresList = [],
    labelsList = [],
    usersList = [],
    stages = [],
    pipelines = [],
    surgeonRanking,
    defaultPipeline,
    currentUser,
    tenantColor,
  } = props;

  const classes = useStyles();

  const [step, setStep] = useState(0);

  const [surgeon, setSurgeon] = useState('');
  const [title, setTitle] = useState('');
  const [date, setDate] = useState(moment());

  const [newSurgeon, setNewSurgeon] = useState(false);
  const [surgeonTitle, setSurgeonTitle] = useState('Dr');
  const [surgeonFirstName, setSurgeonFirstName] = useState('');
  const [surgeonLastName, setSurgeonLastName] = useState('');

  const [anticipatedVolume, setAnticipatedVolume] = useState(anticipatedVolumeTypes.YEARLY.value);
  const [anticipatedValue, setAnticipatedValue] = useState(0);
  const [procedures, setProcedures] = useState([{ id: uuid(), procedureId: null, quantity: undefined }]);
  const [isManualValue, setManualValue] = useState(false);

  const [pipeline, setPipeline] = useState(defaultPipeline);
  const [stage, setStage] = useState(null);

  const [labels, setLabels] = useState([]);
  const [owner, setOwner] = useState(currentUser?.uid || '');

  const [teachingRanking, setTeachingRanking] = useState(3);
  const [kolStatusRanking, setKolStatusRanking] = useState(3);
  const [publishedRanking, setPublishedRanking] = useState(3);
  const [trainingRanking, setTrainingRanking] = useState(3);

  useEffect(() => {
    if (open && defaultPipeline) {
      setPipeline(defaultPipeline);
    }
    if (mode !== 'create' && open && lead) {
      setSurgeon(lead.surgeon || '');
      setTitle(lead.title || '');
      setDate(lead.date || moment());
      setAnticipatedVolume(lead.anticipatedVolume || anticipatedVolumeTypes.YEARLY.value);
      setAnticipatedValue(lead.anticipatedValue || 0);
      setProcedures(lead.procedures && lead.procedures.length ? lead.procedures : [{ id: uuid(), procedureId: null, quantity: undefined }]);
      setManualValue(lead.isManualValue);
      setPipeline(lead.pipeline);
      setStage(lead.stage);
      setLabels(lead.labels);
      setOwner(lead.owner);
      setTeachingRanking(lead?.ranking?.teaching || 3);
      setKolStatusRanking(lead?.ranking?.kolStatus || 3);
      setPublishedRanking(lead?.ranking?.published || 3);
      setTrainingRanking(lead?.ranking?.training || 3);
    }
  }, [mode, open, lead, defaultPipeline]);

  useEffect(() => {
    if (!open) {
      setStep(0);
      setSurgeon('');
      setTitle('');
      setDate(moment());
      setProcedures([{ id: uuid(), procedureId: null, quantity: undefined }]);
      setStage(null);
      setLabels([]);
      setNewSurgeon(false);
      setSurgeonTitle('Dr');
      setSurgeonFirstName('');
      setSurgeonLastName('');
      setTeachingRanking(3);
      setKolStatusRanking(3);
      setPublishedRanking(3);
      setTrainingRanking(3);
      setOwner(currentUser?.uid || '');
    }
  }, [open])

  const calculatedValue = useMemo(() => (
    sum(procedures.map((p) => {
      const procedure = proceduresList?.find((procedure) => procedure.id === p.procedureId);
      const averageValue = procedure?.averageValue || 0;
      const quantity = Number(p?.quantity || 0);

      if (anticipatedVolume === anticipatedVolumeTypes.MONTHLY.value) {
        return averageValue * quantity * 12;
      }

      return averageValue * quantity;
    })) || 0
  ), [proceduresList, procedures, anticipatedVolume]);

  const onAddNewProcedure = () => {
    const arr = [...procedures];
    arr.push({
      id: uuid(),
      procedureId: '',
      quantity: undefined,
    });
    setProcedures(arr);
  };

  const onProcedureDelete = (id) => {
    setProcedures([...procedures].filter((p) => p.id !== id));
  };

  const onProcedureChange = (id, procedureId) => {
    const arr = [...procedures];
    const procedure = arr.find((p) => p.id === id);
    if (procedure) {
      procedure.procedureId = procedureId;
      setProcedures(arr);
    }
  };

  const onQuantityChange = (id, quantity) => {
    const arr = [...procedures];
    const procedure = arr.find((p) => p.id === id);
    if (procedure) {
      procedure.quantity = quantity;
      setProcedures(arr);
    }
  };

  const handleSubmit = () => {
    const data = {
      title,
      surgeon,
      date,
      anticipatedVolume,
      anticipatedValue: isManualValue ? anticipatedValue : calculatedValue,
      isManualValue,
      procedures: procedures.map((p) => ({ ...p, quantity: Number(p?.quantity || 0) })),
      pipeline,
      stage,
      labels,
      owner,
      ranking: {
        teaching: teachingRanking,
        kolStatus: kolStatusRanking,
        published: publishedRanking,
        training: trainingRanking,
      },
    };

    if (newSurgeon) {
      onSubmit(data, { title: surgeonTitle, firstName: surgeonFirstName, lastName: surgeonLastName });
    } else {
      onSubmit(data);
    }
  };

  const isDisabled = useMemo(() => {
    // if (mode === 'reactivate' && pipeline === LOST_LEADS_PIPELINE_ID) {
    //   return true;
    // }

    return (
      !title || !date || !procedures?.length || procedures.some((p) => !p.quantity) || (
        newSurgeon ? (!surgeonFirstName || !surgeonLastName) : !surgeon
      )
    );
  }, [title, date, surgeon, newSurgeon, surgeonFirstName, surgeonLastName, mode, procedures]);

  const surgeonsOptions = useMemo(() => surgeonsList.map((s) => ({
    value: s.id,
    label: `${s.title} ${s.firstName} ${s.lastName}`,
  })), [surgeonsList]);

  const pipelinesOptions = useMemo(() => pipelines.filter((p) => p.id !== LOST_LEADS_PIPELINE_ID && p.id !== ACTIVE_SURGEONS_PIPELINE_ID).map((p) => ({
    value: p.id,
    label: p.title
  })), [pipelines]);

  const stagesOptions = useMemo(() => stages.filter((s) => s.pipelineId === pipeline).map((s) => ({
    value: s.id,
    label: s.title,
  })), [stages, pipeline]);

  const labelsOptions = useMemo(() => labelsList.map((l) => ({
    value: l.id,
    label: l.name,
    icon: <LabelIcon color={l.color} fontSize='small' />
  })), [labelsList]);

  const usersOptions = useMemo(() => usersList.map((u) => ({
    value: u.uid,
    label: `${u.firstName} ${u.lastName}`,
  })), [usersList]);

  const modalTitle = useMemo(() => {
    switch (mode) {
      case 'create':
      default:
        return 'Add Lead';
      case 'update':
        return `Edit ${lead.title}`;
      case 'reactivate':
        return 'Reactivate';
    }
  }, [mode, lead]);

  const modalButtonText = useMemo(() => {
    switch (mode) {
      case 'create':
      default:
        return 'Add Lead';
      case 'update':
        return 'Save Changes';
      case 'reactivate':
        return 'Reactivate';
    }
  }, [mode]);

  const rankingScore = useMemo(() => {
    const sum = teachingRanking + kolStatusRanking + publishedRanking + trainingRanking;
    const _ranking = surgeonRanking || defaultRankingAndActivity;

    if (sum && sum <= 20 && _ranking) {
      return _ranking[sum].rankCategory || 'E';
    }

    return '';
  }, [teachingRanking, kolStatusRanking, publishedRanking, trainingRanking]);

  // const activityTarget = useMemo(() => {
  //     const activity = Object.values(surgeonRanking).filter((ranking) => ranking.rankCategory === rankingScore);
  //     return activity[0]?.activityTarget || '';
  //   },
  //   [rankingScore, surgeonRanking],
  // );

  const renderStep = () => {
    switch (step) {
      case 0:
        return (
          <div className='lead-modal__content'>
            <div>
              <div className='secondary m-b-sm font-size-sm'>Surgeon</div>
              {newSurgeon ? (
                <div className='lead-modal__surgeon-input'>
                  <SelectBox
                    value={surgeonTitle}
                    onChange={setSurgeonTitle}
                    options={titleOptions}
                  />
                  <TextInput
                    value={surgeonFirstName}
                    onChange={(e) => setSurgeonFirstName(e.target.value)}
                    placeholder='First Name'
                  />
                  <TextInput
                    value={surgeonLastName}
                    onChange={(e) => setSurgeonLastName(e.target.value)}
                    placeholder='Last Name'
                  />
                </div>
              ) : (
                <Autocomplete
                  value={surgeon}
                  onChange={setSurgeon}
                  options={surgeonsOptions}
                  placeholder="Start typing to search surgeons"
                  disabled={lead?.status === leadStatuses.WON.value || mode === 'reactivate'}
                />
              )}
              {!newSurgeon && mode === 'create' && (
                <div
                  className='pointer font-size-sm m-t-sm'
                  style={{ color: tenantColor || defaultStaticColor }}
                  onClick={() => setNewSurgeon(true)}
                >
                  Add Surgeon
                </div>
              )}
            </div>

            <div className='lead-modal__field-container'>
              <div className='secondary m-b-sm font-size-sm'>Lead Title</div>
              <TextInput
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder='Add Lead Title'
              />
            </div>

            <div className='lead-modal__field-container'>
              <div className='secondary m-b-sm font-size-sm'>Expected Close Date</div>
              <DatePicker
                date={date}
                onDateChange={setDate}
              />
            </div>

            <div className='lead-modal__field-container'>
              <div className='secondary m-b-sm font-size-sm'>Anticipated Volume</div>
              <div className='lead-modal__anticipated-volume-buttons'>
                <ColorButton
                  className='m-r-md'
                  text={anticipatedVolumeTypes.YEARLY.label}
                  onClick={() => setAnticipatedVolume(anticipatedVolumeTypes.YEARLY.value)}
                  color={anticipatedVolume === anticipatedVolumeTypes.YEARLY.value ? 'blue' : 'grey'}
                />
                <ColorButton
                  className='m-l-md'
                  text={anticipatedVolumeTypes.MONTHLY.label}
                  onClick={() => setAnticipatedVolume(anticipatedVolumeTypes.MONTHLY.value)}
                  color={anticipatedVolume === anticipatedVolumeTypes.MONTHLY.value ? 'blue' : 'grey'}
                />
              </div>

              <div className='m-t-md'>
                {procedures?.map((procedure) => (
                  <LeadProcedureItem
                    key={procedure.id}
                    procedures={proceduresList}
                    procedureId={procedure.procedureId}
                    quantity={procedure.quantity}
                    onProcedureChange={(value) => onProcedureChange(procedure.id, value)}
                    onQuantityChange={(value) => onQuantityChange(procedure.id, value)}
                    onDelete={() => onProcedureDelete(procedure.id)}
                  />
                ))}

                <div className='d-flex space-between align-start'>
                  <div
                    className='pointer font-size-sm'
                    style={{ color: tenantColor || defaultStaticColor }}
                    onClick={onAddNewProcedure}
                  >
                    Add Procedure
                  </div>

                  <div className='lead-modal__calculated-value-input'>
                    <div className='secondary font-size-sm'>Calculated Value</div>

                    <NumberFormat
                      value={isManualValue ? anticipatedValue : calculatedValue}
                      onValueChange={(v) => setAnticipatedValue(v.floatValue)}
                      thousandSeparator={true}
                      prefix={'$'}
                      customInput={TextInput}
                      fixedDecimalScale={0}
                      decimalScale={0}
                      disabled={!isManualValue}
                    />
                  </div>
                </div>

                <div className='d-flex flex-end'>
                  <div className='secondary m-r-md font-size-sm'>
                    Manual Value
                  </div>
                  <Checkbox
                    input={{
                      onClick: (e) => setManualValue(e.target.checked),
                      checked: isManualValue
                    }}
                    fontSize='small'
                  />
                </div>
              </div>
            </div>

            <div className='lead-modal__field-container'>
              <div className='secondary m-b-sm font-size-sm'>Add Labels</div>
              <MultiSelect
                value={labels}
                onChange={setLabels}
                options={labelsOptions}
              />
            </div>

            <div className='d-flex flex-end m-t-lg'>
              <div className='m-r-lg'>
                <ColorButton
                  color='grey'
                  text='Cancel'
                  onClick={onClose}
                />
              </div>
              <ColorButton
                color='blue'
                text='Next'
                onClick={() => setStep(1)}
                disabled={isDisabled}
              />
            </div>
          </div>
        );
      case 1:
        return (
          <div className='lead-modal__content'>
            <div className='lead-modal__field-container' style={{ marginTop: 0 }}>
              <div className='secondary m-b-sm font-size-sm'>Surgeon Ranking (1 = Low, 5 = High)</div>

              <div className='d-flex space-between'>
                <Tooltip
                  title="The time invested & cases performed whilst working and operating at a teaching hospital"
                  aria-label="teaching-hospital-presence"
                  classes={{ tooltip: classes.tooltip }}
                >
                  <div className='font-bold'>Teaching Hospital Presence</div>
                </Tooltip>
                <div style={{ width: 120 }}>
                  <SelectBox
                    value={teachingRanking}
                    onChange={setTeachingRanking}
                    options={rankingOptions}
                  />
                </div>
              </div>

              <div className='m-t-md d-flex space-between'>
                <Tooltip
                  title="Status of the surgeon among peers, relating to experience, accolades, society leadership roles, and speaking"
                  aria-label="teaching-hospital-presence"
                  classes={{ tooltip: classes.tooltip }}
                >
                  <div className='font-bold'>KOL Status/Podium PR (Ambition)</div>
                </Tooltip>
                <div style={{ width: 120 }}>
                  <SelectBox
                    value={kolStatusRanking}
                    onChange={setKolStatusRanking}
                    options={rankingOptions}
                  />
                </div>
              </div>

              <div className='m-t-md d-flex space-between'>
                <Tooltip
                  title="Volume and quality of papers written or co-authored"
                  aria-label="teaching-hospital-presence"
                  classes={{ tooltip: classes.tooltip }}
                >
                  <div className='font-bold'>Published</div>
                </Tooltip>
                <div style={{ width: 120 }}>
                  <SelectBox
                    value={publishedRanking}
                    onChange={setPublishedRanking}
                    options={rankingOptions}
                  />
                </div>
              </div>

              <div className='m-t-md d-flex space-between'>
                <Tooltip
                  title="Level of interest in delivering training around our product to other Surgeons/Registrars"
                  aria-label="teaching-hospital-presence"
                  classes={{ tooltip: classes.tooltip }}
                >
                  <div className='font-bold'>Training Interest</div>
                </Tooltip>
                <div style={{ width: 120 }}>
                  <SelectBox
                    value={trainingRanking}
                    onChange={setTrainingRanking}
                    options={rankingOptions}
                  />
                </div>
              </div>

              <div className="d-flex space-between align-start m-t-lg">
                <div />
                <div className="lead-modal__calculated-value-input">
                  <div className="secondary font-size-sm">Surgeon Ranking</div>

                  <TextInput
                    value={rankingScore}
                    onChange={() => {}}
                    disabled
                  />
                </div>
              </div>

              {/*<div className='m-t-lg'>*/}
              {/*  <Alert severity='warning'>*/}
              {/*    <div className='font-bold'>*/}
              {/*      {`Rank ${rankingScore} – Surgeons with this ranking should have ${activityTarget} activities per month associated to them.`}*/}
              {/*    </div>*/}
              {/*  </Alert>*/}
              {/*</div>*/}
            </div>


            <div className=''>
              <div className='secondary m-b-sm font-size-sm'>Add to Pipeline</div>
              <SelectBox
                value={pipeline}
                onChange={(value) => {
                  setPipeline(value);
                  setStage(null);
                }}
                options={pipelinesOptions}
              />
            </div>

            <div className='lead-modal__field-container'>
              <div className='secondary m-b-sm font-size-sm'>Add to Stage</div>
              <SelectBox
                value={stage}
                onChange={setStage}
                options={stagesOptions}
              />
            </div>

            <div className='lead-modal__field-container'>
              <div className='secondary m-b-sm font-size-sm'>Owner</div>
              <SelectBox
                value={owner}
                onChange={setOwner}
                options={usersOptions}
              />
            </div>

            <div className='d-flex flex-end m-t-lg'>
              <div className='m-r-lg'>
                <ColorButton
                  color='grey'
                  text='Back'
                  onClick={() => setStep(0)}
                />
              </div>
              <ColorButton
                color='blue'
                text={modalButtonText}
                onClick={handleSubmit}
                disabled={loading || !stage || !pipeline}
                loading={loading}
              />
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={modalTitle}
      className='lead-modal__container'
    >
      {renderStep()}
    </Modal>
  )
};

export default LeadModal;
